/**
 * Pure helpers for deciding which rendering "bucket" each virtual link belongs
 * to when building the merged links list on the public bio / live preview.
 *
 * Extracting this logic makes it easy to unit-test the critical conditions
 * independently of React component rendering.
 */

export type ContactBucket = 'positioned' | 'trailing' | 'top-only';

/**
 * Returns which bucket the contact virtual link belongs to:
 *
 * - `'positioned'`  — has an explicit integer position; insert at that slot.
 * - `'trailing'`    — no explicit position and NOT a top-only legacy mode;
 *                     append at the end of the links list.
 * - `'top-only'`    — legacy `email_collection_position === 'top'`; render
 *                     exclusively in the separate section above the list.
 *
 * Note: `email_collection_position === 'both'` renders the button BOTH above
 * the list (top section) AND at the end of the list (trailing), so it falls
 * into the `'trailing'` bucket (the top section is handled separately).
 */
export function getContactBucket({
  contactLinkPosition,
  emailCollectionPosition,
}: {
  contactLinkPosition: number | null | undefined;
  emailCollectionPosition: string | null | undefined;
}): ContactBucket {
  if (contactLinkPosition != null && contactLinkPosition >= 0) {
    return 'positioned';
  }
  if (emailCollectionPosition === 'top') {
    return 'top-only';
  }
  return 'trailing';
}

export type StoreBucket = 'positioned' | 'trailing';

/**
 * Returns which bucket the store virtual link belongs to.
 */
export function getStoreBucket({
  storeLinkPosition,
}: {
  storeLinkPosition: number | null | undefined;
}): StoreBucket {
  if (storeLinkPosition != null && storeLinkPosition >= 0) {
    return 'positioned';
  }
  return 'trailing';
}
